import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { AuthClub, AuthResponse, AuthUser, Role } from '@lcc/shared';
import { api, token } from './api';

interface AuthContextValue {
  user: AuthUser | null;
  /** The club this session belongs to — its name and crest. */
  club: AuthClub | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
  /** Admins pass every role check. */
  can: (...roles: Role[]) => boolean;
  isAdmin: boolean;
  /**
   * Whether this session is the office at all.
   *
   * A plain member has a place here now, but it is not Club Office — it is the
   * member view, which shows only what the companion app shows. Anybody with
   * any role beyond 'member' gets the office; that is the whole test, and it
   * lives here so no screen has to re-derive it.
   */
  isOffice: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [club, setClub] = useState<AuthClub | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token.get()) { setUser(null); setLoading(false); return; }
    try {
      const { user: me, club: mine } =
        await api.get<{ user: AuthUser; club: AuthClub }>('/auth/me');
      setUser(me);
      if (mine) setClub(mine);
    } catch {
      token.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // The API client fires this when a request comes back 401.
  useEffect(() => {
    const onSignedOut = () => setUser(null);
    window.addEventListener('lcc:signed-out', onSignedOut);
    return () => window.removeEventListener('lcc:signed-out', onSignedOut);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<AuthResponse>('/auth/login', { email, password });
    token.set(res.token);
    setUser(res.user);
    if (res.club) setClub(res.club);
  }, []);

  const signOut = useCallback(() => {
    token.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const roles = user?.roles ?? [];
    const isAdmin = roles.includes('admin') || roles.includes('board');
    const isOffice = roles.some((r) => r !== 'member');
    return {
      user, club, loading, signIn, signOut, refresh, isAdmin, isOffice,
      can: (...needed: Role[]) => isAdmin || needed.some((r) => roles.includes(r)),
    };
  }, [user, club, loading, signIn, signOut, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
